import { Chip, Tooltip } from "@mui/material";
import React from "react";

const TIMEZONE = {
    UTC: "UTC",
    LOCAL: "LOCAL",
};

const formatDate = (value, tz) => {
    if (!value) return "—";
    const date = new Date(value);
    if (isNaN(date)) return "—";
    if (tz === TIMEZONE.UTC) {
        return date.toISOString();
    }
    const pad = (n, length = 2) => String(n).padStart(length, "0");
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());
    const milliseconds = pad(date.getMilliseconds(), 3);
    const offset = date.getTimezoneOffset();
    const sign = offset > 0 ? "-" : "+";
    const absOffset = Math.abs(offset);
    const offsetH = pad(Math.floor(absOffset / 60));
    const offsetM = pad(absOffset % 60);
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}${sign}${offsetH}:${offsetM}`;
};

const formatDateHuman = (value, tz) => {
    if (!value) return null;
    const date = new Date(value);
    if (isNaN(date)) return null;
    const tzOptions = tz === TIMEZONE.UTC ? { timeZone: "UTC" } : {};
    const datePart = date
        .toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            ...tzOptions,
        })
        .replace(/ /g, "-");
    const timePart = date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        ...tzOptions,
    });
    return { datePart, timePart };
};

const DateCell = ({ value, tz }) => {
    const formatted = formatDateHuman(value, tz);
    if (!formatted) return <span>—</span>;
    return (
        <div style={{ lineHeight: 1.35, textAlign: "left" }}>
            <div style={{ fontSize: "0.85rem" }}>
                {formatted.datePart}
            </div>
            <div style={{ fontSize: "0.75rem", color: "rgba(0,0,0,0.5)" }}>
                {formatted.timePart}
            </div>
        </div>
    );
};

const getColumnsData = (tz) => {
    return [
        {
            field: "subscriptionAccountingId",
            headerName: "Acct. ID",
            flex: 2,
            sortable: false,
            renderCell: (data) => (
                <Tooltip title={data?.value}>
                    <p style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                        {data?.value || "-"}
                    </p>
                </Tooltip>
            ),
        },
        {
            field: "serialNo",
            headerName: "Serial No.",
            flex: 1.5,
            sortable: false,
            renderCell: (data) => (
                <Tooltip title={data?.value}>
                    <p style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                        {data?.value || "-"}
                    </p>
                </Tooltip>
            ),
        },
        {
            field: "vehicleKey",
            headerName: "Vehicle Key",
            flex: 2,
            sortable: false,
            renderCell: (data) => (
                <Tooltip title={data?.value}>
                    <p style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                        {data?.value || "-"}
                    </p>
                </Tooltip>
            ),
        },
        {
            field: "transactionId",
            headerName: "Transaction ID",
            flex: 1,
            sortable: false,
            renderCell: (data) => (
                <Tooltip title={data?.value}>
                    <p style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                        {data?.value || "-"}
                    </p>
                </Tooltip>
            ),
        },
        {
            field: "pos",
            headerName: "POS",
            flex: 0.5,
            sortable: false,
            renderCell: (data) => (
                <Tooltip title={data?.value}>
                    <p style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                        {data?.value || "-"}
                    </p>
                </Tooltip>
            ),
        },
        {
            field: "subscriptionUpdateMethod",
            headerName: "Update Method",
            flex: 1,
            sortable: false,
            renderCell: (data) => (
                <Tooltip title={data?.value}>
                    <p style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                        {data?.value || "-"}
                    </p>
                </Tooltip>
            ),
        },
        {
            field: "daysAddedOrRemoved",
            headerName: "Days +/-",
            flex: 0.5,
            type: "number",
            renderCell: (data) => {
                const value = data?.value;
                if (value == null) return "-";
                const color = value > 0 ? "success" : value < 0 ? "error" : "default";
                return (
                    <Chip
                        label={value > 0 ? `+${value}` : `${value}`}
                        color={color}
                        size="small"
                        variant="outlined"
                    />
                );
            },
        },
        {
            field: "subscriptionStartDate",
            headerName: "Sub. Start",
            type: "dateTime",
            flex: 1.25,
            valueGetter: (data) =>
                data?.row?.subscriptionStartDate
                    ? new Date(data.row.subscriptionStartDate)
                    : null,
            valueFormatter: ({ value }) => (value ? formatDate(value, tz) : "-"),
            renderCell: (data) => (
                <DateCell value={data?.row?.subscriptionStartDate} tz={tz} />
            ),
        },
        {
            field: "subscriptionEndDate",
            headerName: "Sub. End",
            flex: 1.25,
            type: "dateTime",
            valueGetter: (data) =>
                data?.row?.subscriptionEndDate
                    ? new Date(data.row.subscriptionEndDate)
                    : null,
            valueFormatter: ({ value }) => (value ? formatDate(value, tz) : "-"),
            renderCell: (data) => (
                <DateCell value={data?.row?.subscriptionEndDate} tz={tz} />
            ),
        },
        {
            field: "createdAt",
            headerName: "Created At",
            flex: 1.25,
            type: "dateTime",
            valueGetter: (data) =>
                data?.row?.createdAt ? new Date(data.row.createdAt) : null,
            valueFormatter: ({ value }) => (value ? formatDate(value, tz) : "-"),
            renderCell: (data) => (
                <DateCell value={data?.row?.createdAt} tz={tz} />
            ),
        },
    ];
};

export { getColumnsData, TIMEZONE };